import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import useSwipeToClose from '../../../hooks/useSwipeToClose';
import Spinner from '../../UI/Spinner';

const DietaryDrawer = ({ isOpen, dietaryOptions, selectedDietaryPreferences, setDietaryPreferencesAll, closeDrawer, onSave, isSaving = false }) => {
  const { t } = useTranslation('wizard');
  const [showAdvancedFood, setShowAdvancedFood] = useState(false);
  const [showAdvancedDiet, setShowAdvancedDiet] = useState(false);
  const { containerRef, dragOffset, isAnimating, shouldRender, isVisible, isDragging, DragHandle } = useSwipeToClose(closeDrawer, isOpen);

  // Temporary local state for draft changes
  const [tempSelectedPreferences, setTempSelectedPreferences] = useState(selectedDietaryPreferences);
  const [isLimitExceeded, setIsLimitExceeded] = useState(false);
  const [wiggleTrigger, setWiggleTrigger] = useState(false);

  // Reset temp state when drawer opens (not on close to avoid visual collapse during animation)
  useEffect(() => {
    if (isOpen) {
      setTempSelectedPreferences(selectedDietaryPreferences);
      setShowAdvancedFood(false);
      setShowAdvancedDiet(false);
      setIsLimitExceeded(false);
      setWiggleTrigger(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only depend on isOpen, not selectedDietaryPreferences

  // Reset wiggle trigger after animation finishes
  useEffect(() => {
    if (wiggleTrigger) {
      const timer = setTimeout(() => {
        setWiggleTrigger(false);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [wiggleTrigger]);

  // Helper to check if an advanced option is selected (use temp state)
  const isAdvancedOptionSelected = (option) => 
    option.isAdvanced && tempSelectedPreferences.some(pref => pref.key === option.key);

  // Get food options: non-advanced + selected advanced + (all advanced if expanded), excluding none options
  const getVisibleFoodOptions = () => {
    const foodOptions = dietaryOptions.filter(pref => pref.type === 'food' && !pref.isNoneOption);
    
    if (showAdvancedFood) {
      return foodOptions;
    }
    
    return foodOptions.filter(pref => !pref.isAdvanced || isAdvancedOptionSelected(pref));
  };

  // Get diet options: non-advanced + selected advanced + (all advanced if expanded), excluding none options
  const getVisibleDietOptions = () => {
    const dietOptions = dietaryOptions.filter(pref => pref.type === 'diet' && !pref.isNoneOption);
    
    if (showAdvancedDiet) {
      return dietOptions;
    }
    
    return dietOptions.filter(pref => !pref.isAdvanced || isAdvancedOptionSelected(pref));
  };

  // Check if there are hidden advanced options (excluding none options)
  const hasHiddenAdvancedFood = !showAdvancedFood && 
    dietaryOptions.some(pref => pref.type === 'food' && !pref.isNoneOption && pref.isAdvanced && !isAdvancedOptionSelected(pref));
  
  const hasHiddenAdvancedDiet = !showAdvancedDiet && 
    dietaryOptions.some(pref => pref.type === 'diet' && !pref.isNoneOption && pref.isAdvanced && !isAdvancedOptionSelected(pref));

  // Helper to get the type of a preference by key (handles missing type property from localStorage)
  const getPrefType = (pref) => {
    if (pref.type) return pref.type;
    const fullPref = dietaryOptions.find(opt => opt.key === pref.key);
    return fullPref?.type;
  };

  const MAX_FOOD_OPTIONS = 4;

  // Handle dietary selection (local only)
  const handleTempDietarySelect = (preference) => {
    // Handle None options for each section
    if (preference.key === "noneFood") {
      setIsLimitExceeded(false);
      setTempSelectedPreferences(prev => {
        const withoutFood = prev.filter(pref => getPrefType(pref) !== 'food');
        return [...withoutFood, preference];
      });
      return;
    }
    
    if (preference.key === "noneDiet") {
      setTempSelectedPreferences(prev => {
        const withoutDiet = prev.filter(pref => getPrefType(pref) !== 'diet');
        return [...withoutDiet, preference];
      });
      return;
    }

    // If selecting a specific preference, handle by type
    setTempSelectedPreferences(prev => {
      // Remove the corresponding None option when selecting a real preference
      let newSelection = prev.filter(pref => {
        if (preference.type === 'food') return pref.key !== 'noneFood';
        if (preference.type === 'diet') return pref.key !== 'noneDiet';
        return true;
      });
      
      // Toggle the selected preference
      const isSelected = newSelection.some(pref => pref.key === preference.key);
      if (isSelected) {
        // Deselecting - for diet type, add back the None option
        const filtered = newSelection.filter(pref => pref.key !== preference.key);
        if (preference.type === 'diet') {
          const hasOtherDiet = filtered.some(pref => getPrefType(pref) === 'diet' && !pref.isNoneOption && pref.key !== 'noneDiet');
          if (!hasOtherDiet) {
            const noneDiet = dietaryOptions.find(opt => opt.key === 'noneDiet');
            return noneDiet ? [...filtered, noneDiet] : filtered;
          }
        } else {
          // Food options: check if current foods count becomes less than MAX_FOOD_OPTIONS
          const remainingFoodCount = filtered.filter(pref => getPrefType(pref) === 'food' && pref.key !== 'noneFood').length;
          if (remainingFoodCount < MAX_FOOD_OPTIONS) {
            setIsLimitExceeded(false);
          }
        }
        return filtered;
      } else {
        // Selecting a new preference
        if (preference.type === 'diet') {
          // Diet options: single selection only - remove any other diet options
          const withoutDiets = newSelection.filter(pref => getPrefType(pref) !== 'diet');
          return [...withoutDiets, preference];
        } else {
          const currentFoods = newSelection.filter(pref => getPrefType(pref) === 'food');
          if (currentFoods.length >= MAX_FOOD_OPTIONS) {
            if (!isLimitExceeded) {
              setIsLimitExceeded(true);
              setWiggleTrigger(true);
            }
            const firstFood = currentFoods[0];
            const withoutFirstFood = newSelection.filter(pref => pref.key !== firstFood.key);
            return [...withoutFirstFood, preference];
          }
          return [...newSelection, preference];
        }
      }
    });
  };

  // Save changes to parent and close
  const handleSave = () => {
    // Set all dietary preferences at once
    setDietaryPreferencesAll(tempSelectedPreferences);
    onSave();
  };

  if (!shouldRender) return null;

  return createPortal(
    <div 
      className={`fixed flex items-end justify-center inset-0 bg-[#0D0C07] z-[200] drawer-overlay`} 
      style={{
        backgroundColor: `rgba(13, 12, 7, ${isVisible ? 0.5 : 0})`,
        transition: 'background-color 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      }}
      onClick={closeDrawer}
    >
      <div 
        ref={containerRef}
        className={`flex flex-col w-full bg-[#F8F8F7] rounded-[12px_12px_0px_0px] max-w-5xl mx-auto shadow-lg drawer-content max-h-[92vh]`}
        style={{
          transform: isVisible 
            ? `translateY(${Math.max(0, dragOffset)}px)` 
            : 'translateY(100%)',
          transition: (dragOffset === 0 && isAnimating) || (!isDragging && dragOffset === 0)
            ? 'transform 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)' 
            : 'none'
        }}
        onClick={(e) => e.stopPropagation()}
      >
      {/* Drag Handle */}
      <DragHandle />
      {/* Close button */}
      <button 
        onClick={closeDrawer}
        className="absolute top-1 right-1 flex justify-center items-center md:hover:bg-gray-200 w-[36px] h-[36px] rounded-[12px] transition-colors z-20"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M16.381 6.38079C16.7227 6.03908 17.2776 6.03908 17.6193 6.38079C17.961 6.7225 17.961 7.27737 17.6193 7.61907L13.2385 11.9999L17.6193 16.3808L17.6789 16.4472C17.9595 16.7909 17.9398 17.2986 17.6193 17.6191C17.2989 17.9395 16.7911 17.9592 16.4474 17.6786L16.381 17.6191L12.0002 13.2382L7.61932 17.6191C7.27761 17.9608 6.72275 17.9608 6.38104 17.6191C6.03933 17.2774 6.03933 16.7225 6.38104 16.3808L10.7619 11.9999L6.38104 7.61907L6.32147 7.55267C6.04089 7.20899 6.06059 6.70124 6.38104 6.38079C6.70149 6.06034 7.20923 6.04064 7.55291 6.32122L7.61932 6.38079L12.0002 10.7617L16.381 6.38079Z" fill="#56554D"/>
        </svg>
      </button>
      
      {/* Scrollable content including header */}
      <div className="flex-1 overflow-y-auto min-h-0 overflow-x-hidden p-8 px-6 pb-[116px]">

        {/* Food Preferences Section */}
        <div className="mb-7">
          <h2 className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[16px] font-bold leading-[32px] w-full mb-0">{t('dietary.foodPreferences')}</h2>
          <p className={`${isLimitExceeded ? 'text-[var(--Color-UI-Error,#D40B0B)] font-semibold' : 'text-[#65635D]'} ${wiggleTrigger ? 'animate-wiggle' : ''} text-[14px] font-medium leading-[20px] mb-3 transition-colors`}>{t('dietary.foodPreferencesNote')}</p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {/* None option for Food */}
            {dietaryOptions.filter(pref => pref.key === 'noneFood').map((preference) => (
              <button
                key={preference.key}
                className={`p-3 flex text-left text-[14px] leading-[24px] font-semibold rounded-[12px] border-[0.5px] border-[#0D0C07] ${
                  tempSelectedPreferences.some(pref => pref.key === preference.key)
                    ? 'bg-[#FFCB00] md:hover:bg-[#FFCB00]/90' 
                    : 'bg-white md:hover:bg-[#F8F8F7]'
                }`}
                onClick={() => handleTempDietarySelect(preference)}
              >
                
                <div>
                  <div className="font-semibold">{t('dietary.options.none', preference.label)}</div>
                </div>
              </button>
            ))}
            
            {getVisibleFoodOptions().map((preference) => (
              <button
                key={preference.key}
                className={`p-3 flex text-left text-[14px] leading-[24px] font-semibold rounded-[12px] border-[0.5px] border-[#0D0C07] ${
                  tempSelectedPreferences.some(pref => pref.key === preference.key)
                    ? 'bg-[#FFCB00] md:hover:bg-[#FFCB00]/90' 
                    : 'bg-white md:hover:bg-[#F8F8F7]'
                }`}
                onClick={() => handleTempDietarySelect(preference)}
              >
                <div>
                  <div className="font-semibold">{t(`dietary.options.${preference.key}`, preference.label)}</div>
                   {/*<div className="text-xs text-[#65635D] mt-1 font-normal">{preference.description}</div>*/}
                </div>
              </button>
            ))}
          </div>

          {/* More options link */}
          {hasHiddenAdvancedFood && (
            <button
              onClick={() => setShowAdvancedFood(true)}
              className="text-[#0d0c07] font-semibold md:hover:border-[#0d0c07] text-[14px] leading-[24px] border-b border-[#ffcb00] inline-flex items-center gap-1 mt-4"
            >
              <span>{t('dietary.moreOptions', 'More options')}</span>
              
            </button>
          )}
        </div>

        {/* Diet Preferences Section */}
        <div className="mb-0">
          <h2 className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[16px] font-bold leading-[32px] w-full mb-2">{t('dietary.dietaryPreferences')}</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* None option for Diet */}
            {dietaryOptions.filter(pref => pref.key === 'noneDiet').map((preference) => (
              <button
                key={preference.key}
                className={`p-3 pt-2 flex text-left text-[14px] leading-[24px] font-semibold rounded-[12px] border-[0.5px] border-[#0D0C07] ${
                  tempSelectedPreferences.some(pref => pref.key === preference.key)
                    ? 'bg-[#FFCB00] md:hover:bg-[#FFCB00]/90' 
                    : 'bg-white md:hover:bg-[#F8F8F7]'
                }`}
                onClick={() => handleTempDietarySelect(preference)}
              >
                
                <div>
                  <div className="font-semibold">{t('dietary.options.none', preference.label)}</div>
                  <div className="text-xs text-[#65635D] mt-1 font-normal">{t(`dietary.descriptions.${preference.key}`, preference.description)}</div>
                </div>
              </button>
            ))}
            
            {getVisibleDietOptions().map((preference) => (
              <button
                key={preference.key}
                className={`p-3 pt-2 flex text-left text-[14px] leading-[24px] font-semibold rounded-[12px] border-[0.5px] border-[#0D0C07] ${
                  tempSelectedPreferences.some(pref => pref.key === preference.key)
                    ? 'bg-[#FFCB00] md:hover:bg-[#FFCB00]/90' 
                    : 'bg-white md:hover:bg-[#F8F8F7]'
                }`}
                onClick={() => handleTempDietarySelect(preference)}
              >
                <div>
                  <div className="font-semibold">{t(`dietary.options.${preference.key}`, preference.label)}</div>
                  <div className="text-xs text-[#65635D] mt-1 font-normal">{t(`dietary.descriptions.${preference.key}`, preference.description)}</div>
                </div>
              </button>
            ))}
          </div>

          {/* More options link */}
          {hasHiddenAdvancedDiet && (
            <button
              onClick={() => setShowAdvancedDiet(true)}
              className="text-[#0d0c07] font-semibold md:hover:border-[#0d0c07] text-[14px] leading-[24px] border-b border-[#ffcb00] inline-flex items-center gap-1 mt-4"
            >
              <span>{t('dietary.moreOptions', 'More options')}</span>
              
            </button>
          )}
        </div>
      </div>
      
      {/* Fixed Save Button */}
      <div
          className="
            fixed bottom-[16px] left-[20px] right-[20px]
            py-2 px-2 
            flex justify-center
            bg-[linear-gradient(135deg,_rgba(255,255,255,0.6)_0%,_rgba(255,255,255,0.8)_100%)]
            backdrop-blur-lg
            z-50
            max-w-5xl
            mx-auto
            rounded-[96px]
            border-[0.5px] border-[linear-gradient(145deg,_rgba(255,255,255,0.8)_0%,_rgba(255,255,255,1)_100%)]
            shadow-[0px_0px_4px_0px_rgba(13,12,7,0.1),_1px_-2px_16px_0px_rgba(13,12,7,0.1)]
          " 
        >
        <div className="w-full max-w-5xl">
          <button
            className={`
              inline-flex justify-center items-center gap-1
              h-[48px] px-[24px] py-[14px]
              rounded-[32px]
              border-[1px] border-[linear-gradient(200deg,_#090_0%,_#060_100%)]
              bg-[#090]
              text-[#FFF] text-center
              text-[14px] font-medium leading-[28px]
              transition-colors
              w-full
              shadow-[0_2px_16px_0_rgba(0,0,0,0.12)_inset,_0_0_32px_0_rgba(255,255,255,0.4)_inset,_0_0px_0px_2px_rgba(0,102,0,0.4)]
              ${
                isSaving
                  ? "cursor-not-allowed opacity-60 bg-[#090]"
                  : "cursor-pointer bg-[linear-gradient(200deg,_#090_0%,_#060_100%)] md:hover:border-[0.5px] md:hover:border-[#060] md:hover:bg-[#060]"
              }
            `}
            onClick={isSaving ? undefined : handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Spinner size="sm" color="#FFF" />
                <span>{t('common:buttons.saving', 'Saving...')}</span>
              </>
            ) : (
              <span>{t('common:buttons.save', 'Save')}</span>
            )}
          </button>
        </div>
      </div>


      </div>
    </div>, document.body
  );
};

export default DietaryDrawer;
